import { defaultValue, nameInitializer, stringSplitter, buildAssetUrl } from './helpers';
const kScale = window.devicePixelRatio;
export default class AvatarCollection {
    constructor(hud, playheads) {
        this.avatars = [];
        this.hud = hud;
        this.playheads = playheads;
    }
    onActivityAdded(activity) {
        const activityId = activity.activityId;
        const options = Object.assign(Object.assign({}, activity.avatar), { color: activity.color });
        const playhead = this.playheads.get(activityId);
        if (!playhead)
            throw new Error('Expected this activity to have a Playhead');
        const avatar = createAvatar(this.hud, playhead, activityId, options);
        this.avatars.push(avatar);
    }
    onActivityDeleted(activityId) {
        const idx = this.avatars.findIndex(a => a.activityId === activityId);
        if (idx === -1)
            throw new Error(`Avatar for activity ${activityId} does not exist`);
        deleteAvatar(this.hud, this.avatars[idx]);
        this.avatars.splice(idx, 1);
    }
    update(store) {
        const activities = store.getState().activities;
        for (let i = 0; i < this.avatars.length; i++) {
            const avatar = this.avatars[i];
            // Sync the show state with the activity
            const show = activities[avatar.activityId].show;
            // Render
            if (avatar.playhead.ready) {
                if (avatar.text) {
                    avatar.text.setShow(show);
                    avatar.text.setWorldPos(avatar.playhead.cartesian);
                }
                if (avatar.pin) {
                    avatar.pin.setShow(show);
                    avatar.pin.setWorldPos(avatar.playhead.cartesian);
                }
            }
        }
    }
}
function createAvatar(hud, playhead, activityId, options) {
    const { color, image, name, formatName } = options;
    const avatar = { options };
    // Build an SVG representing the Avatar Pin, and hand it to the HUD
    avatar.pinPromise = getAvatarCanvas(image, color).then(avatarPin => {
        avatar.pin = hud.createElement(`AvatarPin[${activityId}]`);
        avatar.pin.setCanvas(avatarPin);
        avatar.pin.setScale(1.0 / kScale);
    });
    // If the Avatar has a name, build a text label and display it above the Pin
    if (name) {
        const nameString = getAvatarName(name, formatName);
        avatar.text = hud.createElement(`AvatarText[${activityId}]`);
        avatar.text.setCanvas(getTextCanvas(nameString));
        avatar.text.setOffset(0, -45);
        avatar.text.setScale(1.0 / kScale);
    }
    avatar.activityId = activityId;
    avatar.playhead = playhead;
    return avatar;
}
function deleteAvatar(hud, avatar) {
    avatar.pinPromise.then(() => {
        hud.removeElement(avatar.pin);
    });
    if (avatar.text) {
        hud.removeElement(avatar.text);
    }
}
function getAvatarName(name, formatName) {
    // Format the name, splitting into multiple lines if necessary
    let nameString = nameInitializer(name, formatName);
    if (nameString.length >= 20) {
        nameString = stringSplitter(nameString);
        if (nameString[1].length > 18)
            nameString[1] = `${nameString[1].substring(0, 14)}...`;
    }
    return nameString;
}
function getAvatarCanvas(imageUrl, color) {
    const defaultAvatarURL = buildAssetUrl('assets/images/logos/_a_transparent_cors.svg');
    const pinWidth = 32.11;
    const pinHeight = 45;
    const canvas = document.createElement('canvas');
    canvas.width = 32 * kScale;
    canvas.height = 45 * kScale;
    const ctx = canvas.getContext('2d');
    ctx.scale(canvas.width / pinWidth, canvas.height / pinHeight);
    const getAvatar = image => {
        const avatarWidth = 14;
        const placeImage = pinWidth / 2;
        const bcurve = 40;
        const pin = { x: pinWidth / 2, y: pinHeight, color: color };
        // Draw the pin background
        ctx.save();
        ctx.translate(pin.x, pin.y);
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.bezierCurveTo(0, -10, -bcurve + 6, -bcurve + 5, 0, -bcurve);
        ctx.bezierCurveTo(bcurve - 6, -bcurve + 5, 0, -10, 0, 0);
        ctx.arc(0, -30, 15, 0, 2 * Math.PI);
        ctx.fillStyle = pin.color;
        ctx.fill();
        ctx.restore();
        // Draw the image
        if (image) {
            ctx.beginPath();
            ctx.arc(placeImage, avatarWidth + 1, avatarWidth, 0, Math.PI * 2, true);
            ctx.closePath();
            ctx.clip();
            ctx.drawImage(image, placeImage - 16, 0, avatarWidth * 2 + 2, avatarWidth * 2 + 2);
        }
        return canvas;
    };
    // Load the avatar image
    const avatarImagePromise = new Promise(resolve => {
        const avImg = new Image();
        avImg.crossOrigin = 'anonymous';
        avImg.onload = () => resolve(avImg);
        avImg.onerror = () => {
            // svg errored, if it was the defaultImg, so return without an image
            if (avImg.src.includes(defaultAvatarURL)) {
                console.error(`Failed to download default Avatar image. No Avatar image will be shown.`);
                resolve();
            }
            else {
                // Set the img.src again, but set it to the defaultImg and try again
                console.warn(`Failed to download Avatar image from ${avImg.src}. Reverting to default image.`);
                avImg.src = defaultAvatarURL;
            }
        };
        avImg.src = defaultValue(imageUrl, defaultAvatarURL);
    });
    return avatarImagePromise.then(avImg => getAvatar(avImg));
}
function getTextCanvas(text) {
    const outlineWidthPx = 2;
    // Options vars
    const lines = Array.isArray(text) ? text : text.split('\n');
    const font = '14px Arial Black, Arial, sans-serif';
    // Create a canvas that will cache the text
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    // Font styling
    ctx.fillStyle = '#E3E3E3';
    // Size the canvas to fit the text
    ctx.font = font;
    const lineHeight = parseInt(font, 10) * 1.15 + outlineWidthPx;
    const width = Math.max.apply(null, lines.map(t => ctx.measureText(t).width)) + outlineWidthPx * 2;
    const height = lines.length * lineHeight;
    canvas.width = width * kScale;
    canvas.height = height * kScale;
    ctx.scale(kScale, kScale);
    ctx.font = font;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillStyle = 'white';
    ctx.strokeStyle = 'black';
    ctx.lineJoin = 'bevel';
    ctx.lineWidth = outlineWidthPx;
    for (let i = 0; i < lines.length; i++) {
        ctx.strokeText(lines[i], width * 0.5, lineHeight * (i + 0.5));
        ctx.fillText(lines[i], width * 0.5, lineHeight * (i + 0.5));
    }
    return canvas;
}
